import React, { useEffect, useRef, useState } from "react";
import { AddWhiteIcon } from "../../../assets";
import {
  FREIGHT_CLASS,
  ITEM_CARD_FIELD_LABELS,
  ITEM_DIMENSION_KEYS,
  ITEM_FIELD_LABELS,
  PACKAGE_FIELD_LABELS,
  VALIDATION_FOCUS_ATTR,
} from "../../../utils/constants";
import { mandatoryMessage } from "../../../utils/orderValidation";
import AddHazmatDetailsModal from "../../modals/AddHazmatDetailsModal";
import { SingleDropdown } from "../../../components/shared";
import AddressDropDown from "../../../components/shared/AddressDropDown";
import { cmToInch, kgToLbs } from "../../../utils/utils";

interface Data {
  [key: string]: any;
}

interface ActionConfigTypes {
  apiUrl: string;
  urlID: string;
  successMessage: string;
  heading?: string;
  para?: string;
}
interface Column {
  header: string;
  key: string;
  count?: boolean;
  sortable?: boolean;
  className: string;
  type?: string;
  children?: ColumnChildren[];
  mandatory?: boolean;
  editColumn?: boolean;
  dropdown?: boolean;
  dropdownData?: any;
  dolloar?: boolean;
  checkbox?: boolean;
  subText?: string;
  isActive?: boolean;
}

interface ColumnChildren {
  header: string;
  key: string;
  type?: string;
}

interface OrderHistoryDetailTableProps<T> {
  columns: Column[];
  packageType: { id: string; name: string }[];
  data: Data[];
  setData: (data: any) => void;
  setLoading: (loading: boolean) => void;
  onDeleteConfig?: ActionConfigTypes;
  isEditable?: boolean;
  showDelete?: boolean;
  setFields?: (field: any) => void;
  getApiCall?: () => void;
  setHasChanges?: (hasChange: boolean) => void;
  deleteFromDataOnEdit?: boolean;
  editIcon?: boolean;
  handleEditModalOpen?: (row: any) => void;
  selectAllCheckboxes?: boolean;
  CenterClassColumnKey?: string[];
  isLTL?: boolean;
  checkAmountChange?: (hasChange: boolean) => void;
  orderType?: string | null;
  customerId?: string;
  /** Row index -> column keys flagged by the mandatory-field validation. */
  invalidCells?: Record<number, string[]>;
  /** Row index -> package index -> column keys, for problems in package sub-rows. */
  invalidPackageCells?: Record<number, Record<number, string[]>>;
  /** Scrolls to and focuses this cell so the user can start typing straight away. */
  focusCell?: { rowIndex: number; columnKey: string } | null;
  /** Anchor name for the mandatory-field validation to scroll to. Opt-in. */
  fieldKey?: string;
  /**
   * Shown when the validation ran against an empty table. There is no cell to outline in
   * that case, so without this the user is scrolled to a blank table with no explanation.
   */
  emptyMessage?: string;
}

interface ConfirmTextTypes {
  heading: string;
  para: string;
}

const isEmpty = (v: any) => !v || `${v}`.trim() === "";

// Show descriptions in the dropdown
const addressColumns = [{ key: "address", title: "Address" }];

const OrderItemDetailTable = <T extends { [key: string]: any }>({
  columns,
  packageType,
  data,
  setData,
  setLoading,
  onDeleteConfig,
  showDelete,
  isLTL,
  isEditable = true,
  setFields,
  checkAmountChange,
  orderType,
  customerId,
  invalidCells,
  invalidPackageCells,
  focusCell,
  fieldKey,
  emptyMessage,
}: OrderHistoryDetailTableProps<T>) => {
  const [rowData, setRowData] = useState<any | null>(null);
  const [rowHandlerType, setRowHandlerType] = useState<string>("");
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [confirmText, setConfirmText] = useState<ConfirmTextTypes>({
    heading: "",
    para: "",
  });
  const [confirmSuccessText, setConfirmSuccessText] = useState<string>("");
  const [rowIndex, setRowIndex] = useState<number>(0);
  const [addHazmatDetailsModal, setAddHazmatDetailsModal] =
    useState<boolean>(false);

  // local lists for dropdown options
  const [descListByRow, setDescListByRow] = useState<Record<number, any[]>>({});
  const [descListByPkg, setDescListByPkg] = useState<Record<string, any[]>>({});

  const makeRowDescSetter =
    (idx: number) =>
      (next: any[] | ((prev: any[]) => any[])) => {
        setDescListByRow((prev) => {
          const prevList = prev[idx] ?? [];
          const resolved =
            typeof next === "function" ? (next as (p: any[]) => any[])(prevList) : next || [];
          return { ...prev, [idx]: resolved };
        });
      };

  const makePkgDescSetter =
    (key: string) =>
      (next: any[] | ((prev: any[]) => any[])) => {
        setDescListByPkg((prev) => {
          const prevList = prev[key] ?? [];
          const resolved =
            typeof next === "function" ? (next as (p: any[]) => any[])(prevList) : next || [];
          return { ...prev, [key]: resolved };
        });
      };

  const stackMapping: any = { yes: "Yes", no: "No" };

  /* ----------------------------- Helpers ----------------------------- */

  const calculateTotalForPackage = (row: any) => {
    let totalWeight = 0;
    let totalQuantity = 0;
    row?.package?.forEach((item: any) => {
      totalWeight += parseFloat(item?.weight === "" ? 0 : item?.weight);
      totalQuantity += parseFloat(item?.quantity === "" ? 0 : item?.quantity);
    });
    return { totalWeight, totalQuantity };
  };

  const calculateFrightClass = (rowData: Data) => {
    let height =
      rowData.weightUnit === "kgs" || rowData.weightUnit?.name === "kgs"
        ? cmToInch(rowData.itemDimension.height)
        : rowData.itemDimension.height,
      width =
        rowData.weightUnit === "kgs" || rowData.weightUnit?.name === "kgs"
          ? cmToInch(rowData.itemDimension.width)
          : rowData.itemDimension.width,
      length =
        rowData.weightUnit === "kgs" || rowData.weightUnit?.name === "kgs"
          ? cmToInch(rowData.itemDimension.length)
          : rowData.itemDimension.length,
      weight =
        rowData.weightUnit === "kgs" || rowData.weightUnit?.name === "kgs"
          ? kgToLbs(rowData.weight)
          : rowData.weight;

    const desnsity = (length * width * height) / 1728;
    const linearFeet = weight / desnsity / rowData.handlingUnit;
    let freightclass = 0;
    if (linearFeet >= 0 && linearFeet < 1) freightclass = 500;
    else if (linearFeet > 1 && linearFeet <= 2) freightclass = 400;
    else if (linearFeet > 2 && linearFeet <= 3) freightclass = 300;
    else if (linearFeet > 3 && linearFeet <= 4) freightclass = 250;
    else if (linearFeet > 4 && linearFeet <= 5) freightclass = 200;
    else if (linearFeet > 5 && linearFeet <= 6) freightclass = 175;
    else if (linearFeet > 6 && linearFeet <= 7) freightclass = 150;
    else if (linearFeet > 7 && linearFeet <= 8) freightclass = 125;
    else if (linearFeet > 8 && linearFeet <= 9) freightclass = 110;
    else if (linearFeet > 9 && linearFeet <= 10.5) freightclass = 100;
    else if (linearFeet > 10.5 && linearFeet <= 12) freightclass = 92.5;
    else if (linearFeet > 12 && linearFeet <= 13.5) freightclass = 85;
    else if (linearFeet > 13.5 && linearFeet <= 15) freightclass = 77.5;
    else if (linearFeet > 15 && linearFeet <= 22.5) freightclass = 70;
    else if (linearFeet > 22.5 && linearFeet <= 30) freightclass = 65;
    else if (linearFeet > 30 && linearFeet <= 35) freightclass = 60;
    else if (linearFeet > 35 && linearFeet <= 50) freightclass = 55;
    else if (linearFeet > 50) freightclass = 50;
    return [freightclass, linearFeet];
  };

  const calculateCBM = (rowData: Data) => {
    if (
      (rowData?.unit &&
        rowData.unit?.name &&
        rowData.unit.name.toString().toLowerCase() === "in") ||
      (rowData?.unit && typeof rowData.unit === "string" && rowData.unit.toString().toLowerCase() === "in")
    ) {
      return (
        (rowData.itemDimension.length *
          rowData.itemDimension.width *
          rowData.itemDimension.height) / 61023.8
      );
    } else if (orderType === "LTL" || orderType === "LTL + Final Mile") {
      return (
        (cmToInch(rowData.itemDimension.length) *
          cmToInch(rowData.itemDimension.width) *
          cmToInch(rowData.itemDimension.height)) / 61023.8
      );
    } else {
      return (
        (rowData.itemDimension.length *
          rowData.itemDimension.width *
          rowData.itemDimension.height) / 1000000
      );
    }
  };

  const calculateLinearFeet = (rowData: Data): number[] => {
    let val = 0;
    if (rowData.weightUnit === "kgs" || rowData.weightUnit?.name === "kgs") {
      if (rowData?.itemDimension?.length != "" && rowData?.handlingUnit != "") {
        let length = cmToInch(rowData.itemDimension.length);
        val =
          (length * rowData.handlingUnit) /
          (cmToInch(rowData?.itemDimension?.width) > 48 ? 12 : 24);
        const num = Number(rowData.handlingUnit);
        if (
          (rowData.stackable === "Yes" || rowData.stackable?.id === "Yes") &&
          num > 10
        ) {
          val = val / 2;
        }
      }
    } else {
      if (rowData?.itemDimension?.length != "" && rowData?.handlingUnit != "") {
        val =
          (rowData.itemDimension.length * rowData.handlingUnit) /
          (rowData?.itemDimension?.width > 48 ? 12 : 24);
        const num = Number(rowData.handlingUnit);
        if (
          (rowData.stackable === "Yes" || rowData.stackable?.id === "Yes") &&
          num > 10
        ) {
          val = val / 2;
        }
      }
    }

    let classVal = 500,
      density = 0,
      cbm = 0;
    if (
      rowData?.itemDimension?.length != "" &&
      rowData?.itemDimension?.width != "" &&
      rowData?.itemDimension?.height != "" &&
      rowData?.handlingUnit != "" &&
      rowData?.weight != ""
    ) {
      [classVal, density] = calculateFrightClass(rowData);
    }

    if (
      rowData?.itemDimension?.length != "" &&
      rowData?.itemDimension?.width != "" &&
      rowData?.itemDimension?.height != ""
    ) {
      cbm = calculateCBM(rowData);
    }
    return [parseFloat(val.toFixed(2)), classVal, density, cbm];
  };

  // Convert a selected template (from customeritemlist) -> row shape
  const applyTemplateToRow = (row: any, tpl: any) => {
    const out = { ...row };

    const toStr = (v: any) =>
      v === undefined || v === null ? "" : `${v}`;

    // direct scalar fields
    if (tpl.packageType != null) out.packageType = typeof tpl.packageType === "string" ? tpl.packageType : (tpl.packageType?.name ?? tpl.packageType?.id ?? "");
    if (tpl.handlingUnit != null) out.handlingUnit = toStr(tpl.handlingUnit);
    if (tpl.quantity != null) out.quantity = toStr(tpl.quantity);
    if (tpl.weight != null) out.weight = toStr(tpl.weight);
    if (tpl.itemClass != null) out.itemClass = toStr(tpl.itemClass);
    if (tpl.nmfc != null) out.nmfc = tpl.nmfc;
    if (tpl.skuNumber != null) out.skuNumber = tpl.skuNumber;

    // units
    const toUnitObj = (u: string | undefined | null) =>
      u
        ? { id: String(u).toUpperCase(), name: String(u).toUpperCase() }
        : out.unit;
    const toWeightUnitObj = (wu: string | undefined | null) =>
      wu
        ? { id: String(wu).toLowerCase(), name: String(wu).toLowerCase() }
        : out.weightUnit;

    if (tpl.unit) out.unit = toUnitObj(tpl.unit);
    if (tpl.weightUnit) out.weightUnit = toWeightUnitObj(tpl.weightUnit);

    // stackable
    if (typeof tpl.stackable === "boolean") {
      const val = tpl.stackable ? "Yes" : "No";
      out.stackable = { id: val, name: val };
    } else if (tpl.stackable?.id || tpl.stackable === "Yes" || tpl.stackable === "No") {
      const val = tpl.stackable?.id ?? tpl.stackable;
      out.stackable = { id: val, name: val };
    }

    // dimensions
    out.itemDimension = out.itemDimension || { length: "", width: "", height: "" };
    if (tpl.length != null) out.itemDimension.length = toStr(tpl.length);
    if (tpl.width != null) out.itemDimension.width = toStr(tpl.width);
    if (tpl.height != null) out.itemDimension.height = toStr(tpl.height);

    // recompute LTL metrics & suggested class
    const [linearFeet, suggestedClass, density, cbm] = calculateLinearFeet(out);
    out.linearFeet = linearFeet;
    out.suggestedClass = suggestedClass;
    out.density = density;
    out.cbm = cbm;

    return out;
  };

  // Convert template -> package row shape
  const applyTemplateToPackageRow = (pkgRow: any, tpl: any) => {
    const out = { ...pkgRow };
    const toStr = (v: any) =>
      v === undefined || v === null ? "" : `${v}`;
    if (tpl.description != null) out.description = tpl.description;
    if (tpl.quantity != null) out.quantity = toStr(tpl.quantity);
    if (tpl.weight != null) out.weight = toStr(tpl.weight);
    if (tpl.length != null) out.length = toStr(tpl.length);
    if (tpl.width != null) out.width = toStr(tpl.width);
    if (tpl.height != null) out.height = toStr(tpl.height);
    if (tpl.itemClass != null) out.itemClass = toStr(tpl.itemClass);
    if (tpl.nmfc != null) out.nmfc = tpl.nmfc;
    if (tpl.skuNumber != null) out.skuNumber = tpl.skuNumber;
    return out;
  };

  /* ---------------------------- Handlers ---------------------------- */

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number
  ) => {
    const { value, type, checked, id } = e.target;
    const newData = [...data];

    if (type === "checkbox") {
      newData[rowIndex][id] = checked;
    } else if (type === "number" && parseFloat(value) < 0) {
      return;
    } else {
      newData[rowIndex][id] = value;
      if (isLTL && id !== "itemClass") {
        const [linearFeet, freightClass, density, cbm] =
          calculateLinearFeet(newData[rowIndex]);
        newData[rowIndex]["linearFeet"] = linearFeet;
        newData[rowIndex]["suggestedClass"] = freightClass;
        newData[rowIndex]["density"] = density;
        newData[rowIndex]["cbm"] = cbm;
      }
    }

    if (id === "amount") {
      checkAmountChange && checkAmountChange(true);
    } else {
      checkAmountChange && checkAmountChange(false);
    }

    newData[rowIndex].isModified = true;
    setData(newData);
  };

  const handleInputChangeForPackage = (
    e: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number,
    pakageRowIndex: number
  ) => {
    const { value, type, id } = e.target;
    const newData = [...data];

    if (type === "number" && parseFloat(value) < 0) return;

    newData[rowIndex]["package"][pakageRowIndex][id] = value;
    const row = newData[rowIndex]["package"][pakageRowIndex];

    if (isLTL && id !== "itemClass") {
      if (id === "weight" || id === "quantity") {
        const totals = calculateTotalForPackage(newData[rowIndex]);
        newData[rowIndex].quantity = totals.totalQuantity;
        newData[rowIndex].weight = totals.totalWeight;
      }
      const [freightClass] = calculateFrightClass({
        itemDimension: {
          length: row.length || "0",
          width: row.width || "0",
          height: row.height || "0",
        },
        weight: row.weight || "0",
        handlingUnit: row.quantity || "0",
      });
      newData[rowIndex]["package"][pakageRowIndex]["suggestedClass"] =
        freightClass;
    }

    newData[rowIndex].isModified = true;
    setData(newData);
  };

  const handleChildInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number
  ) => {
    const { value, type, id } = e.target;
    const newData = [...data];

    if (type === "number" && parseFloat(value) < 0) return;

    newData[rowIndex]["itemDimension"][id] = value;
    if (isLTL) {
      const [linearFeet, freightClass, density, cbm] =
        calculateLinearFeet(newData[rowIndex]);
      newData[rowIndex]["linearFeet"] = linearFeet;
      newData[rowIndex]["suggestedClass"] = freightClass;
      newData[rowIndex]["density"] = density;
      newData[rowIndex]["cbm"] = cbm;
    }

    if (id === "amount") {
      checkAmountChange && checkAmountChange(true);
    } else {
      checkAmountChange && checkAmountChange(false);
    }
    newData[rowIndex].isModified = true;
    setData(newData);
  };

  const handleAction = (
    row: any,
    actionType: string,
    confirmMsgheading: string,
    confirmMsgheadPara: string,
    successMsg: string
  ) => {
    setRowData(row);
    setRowHandlerType(actionType);
    setConfirmText({ heading: confirmMsgheading, para: confirmMsgheadPara });
    setConfirmSuccessText(successMsg);
    setConfirmModal(true);
  };

  const handleHazmatCheckboxChange = (rowIndex: number) => {
    const newData = [...data];
    newData[rowIndex]["hazmat"] = !newData[rowIndex]["hazmat"];
    setData(newData);
  };

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    rowIndex: number,
    row: any
  ) => {
    e.stopPropagation();
    if (row.id === "") {
      const newData = data.filter((_, index) => index !== rowIndex);
      newData.forEach((item, idx) => (item.count = idx + 1));
      newData.forEach((item, idx) => (item.serial = newData.length - idx));
      setData(newData);
      checkAmountChange && checkAmountChange(true);
    } else {
      handleAction(
        row,
        "Delete",
        onDeleteConfig?.heading || "Delete!",
        `delete ${onDeleteConfig?.para}`,
        onDeleteConfig?.successMessage || ""
      );
    }
  };

  const calculateTotals = (rows: Data[]) => {
    let totalHandlingUnits = 0;
    let totalWeight = 0;
    let totalPieces = 0;
    let totalFeet = 0;
    let totaldensity = 0;
    let totalcbm = 0;
    rows?.forEach((item) => {
      const handlingUnits = parseFloat(item?.handlingUnit) || 0;
      const quantity = parseFloat(item?.quantity) || 0;
      const weight = parseFloat(item?.weight) || 0;
      const linearFeet = parseFloat(item?.linearFeet) || 0;
      const density = parseFloat(item?.density) || 0;
      const cbm = parseFloat(item?.cbm) || 0;
      const itemWeight = weight;

      totalHandlingUnits += handlingUnits;
      totalWeight +=
        item.weightUnit === "kgs" || item.weightUnit?.name === "kgs"
          ? kgToLbs(itemWeight)
          : itemWeight;
      totalPieces += quantity;
      totalFeet += linearFeet;
      totaldensity += density;
      totalcbm += cbm;
    });

    return {
      handlingUnits: totalHandlingUnits,
      weight: totalWeight,
      pieces: totalPieces,
      totalFeet,
      density: totaldensity,
      cbm: totalcbm,
    };
  };

  useEffect(() => {
    const totals = calculateTotals(data);
    setFields &&
      setFields((prev: any) => ({
        ...prev,
        destinationHandingUnits: totals.handlingUnits,
        destinationWeight: totals.weight,
        destinationPieces: totals.pieces,
        totalLinearFeet: totals.totalFeet,
        totalDensity: totals.density,
        totalCBM: totals.cbm,
      }));
  }, [data, setFields]);

  useEffect(() => {
    data.forEach((item) => {
      if (item?.itemDimension?.length && item?.itemDimension?.width && item?.itemDimension?.height) {
        item.cbm = calculateCBM(item);
      }
    });
  }, [orderType, data]);

  const handleDropdownChange = (
    e: React.ChangeEvent<HTMLSelectElement> | any,
    rowIndex: number
  ) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();

    const { value, id } = e.target;
    const newData = [...data];
    if (value) {
      if (id === "packageType") {
        if (value !== "PALLET" && value !== "PLT" && value !== "SKID") {
          newData[rowIndex].package = [];
        }
        newData[rowIndex][id] = value;
      } else if (id === "itemClass") {
        newData[rowIndex][id] = value;
      } else if (id === "weightUnit") {
        newData[rowIndex][id] = { name: value, id: value };
        newData[rowIndex]["unit"] = value === "lbs" ? { name: "IN", id: "IN" } : { name: "CM", id: "CM" };
      } else {
        newData[rowIndex][id] = { name: value, id: value };
      }
      if (isLTL && id !== "itemClass" && id !== "packageType") {
        const [linearFeet, freightclass, density, cbm] =
          calculateLinearFeet(newData[rowIndex]);
        newData[rowIndex]["linearFeet"] = linearFeet;
        newData[rowIndex]["suggestedClass"] = freightclass;
        newData[rowIndex]["density"] = density;
        newData[rowIndex]["cbm"] = cbm;
      }
    }
    newData[rowIndex].isModified = true;
    setData(newData);
  };

  const handlePackageDropdownChange = (
    e: React.ChangeEvent<HTMLSelectElement> | any,
    rowIndex: number,
    packageRowIndex: number
  ) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    const { value, id } = e.target;
    const newData = [...data];
    if (value && id === "itemClass") {
      newData[rowIndex].package[packageRowIndex][id] = value;
    }
    newData[rowIndex].isModified = true;
    setData(newData);
  };

  const addPackage = (rowIndex: number) => {
    const newData = [...data];
    newData[rowIndex].package.push({
      quantity: "",
      weight: "",
      length: "",
      width: "",
      height: "",
      itemClass: "",
      nmfc: "",
      description: "",
      skuNumber: "",
    });
    newData[rowIndex].isModified = true;
    setData(newData);
  };

  const handlePackageDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    rowIndex: number,
    packageRowIndex: number
  ) => {
    e.stopPropagation();
    const row = data[rowIndex];
    const newPkg = row.package.filter((_: any, idx: number) => idx !== packageRowIndex);
    row.package = newPkg;
    const finalData = [...data];
    finalData[rowIndex] = row;
    if (newPkg.length > 0) {
      const totals = calculateTotalForPackage(row);
      finalData[rowIndex].quantity =
        totals.totalQuantity > 0 ? totals.totalQuantity : finalData[rowIndex].quantity;
      finalData[rowIndex].weight =
        totals.totalWeight > 0 ? totals.totalWeight.toString() : finalData[rowIndex].weight;
    }
    setData(finalData);
    checkAmountChange && checkAmountChange(true);
  };

  // helpers for local option lists
  const getRowDescList = (idx: number) => descListByRow[idx] || [];
  const getPkgDescList = (key: string) => descListByPkg[key] || [];

  /* ------------- Mandatory-field validation feedback ------------- */

  const rootRef = useRef<HTMLDivElement>(null);

  const isCellInvalid = (index: number, key: string): boolean =>
    !!invalidCells?.[index]?.includes(key);

  const cellErrorClass = (index: number, key: string): string =>
    isCellInvalid(index, key) ? " form__input--error" : "";

  const cellMessage = (index: number, key: string): string | undefined =>
    isCellInvalid(index, key)
      ? mandatoryMessage(
          ITEM_CARD_FIELD_LABELS[key] || ITEM_FIELD_LABELS[key] || key
        )
      : undefined;

  /**
   * Package sub-rows carry their own all-or-nothing rule: blank rows are ignored, but
   * once one value is filled the other two are required. Flagged on the package's own
   * cells so the message never points at the parent item's Description.
   */
  const isPackageCellInvalid = (
    index: number,
    packageIndex: number,
    key: string
  ): boolean => !!invalidPackageCells?.[index]?.[packageIndex]?.includes(key);

  const packageCellErrorClass = (
    index: number,
    packageIndex: number,
    key: string
  ): string => (isPackageCellInvalid(index, packageIndex, key) ? " form__input--error" : "");

  const packageCellMessage = (
    index: number,
    packageIndex: number,
    key: string
  ): string | undefined =>
    isPackageCellInvalid(index, packageIndex, key)
      ? mandatoryMessage(PACKAGE_FIELD_LABELS[key] || key)
      : undefined;

  const packageCellError = (index: number, packageIndex: number, key: string) => {
    const message = packageCellMessage(index, packageIndex, key);
    return message ? <span className="form__error">{message}</span> : null;
  };

  /** Inline message beneath a plain input, mirroring what the dropdowns render. */
  const cellError = (index: number, key: string) => {
    const message = cellMessage(index, key);
    return message ? <span className="form__error">{message}</span> : null;
  };

  /** The three dimension inputs share one label, so they share one message. */
  const dimensionError = (index: number) => {
    const missing = ITEM_DIMENSION_KEYS.some((key) =>
      isCellInvalid(index, key)
    );
    return missing ? (
      <span className="form__error">
        {mandatoryMessage(ITEM_CARD_FIELD_LABELS.itemDimension)}
      </span>
    ) : null;
  };

  useEffect(() => {
    if (!focusCell) return;
    const frame = window.requestAnimationFrame(() => {
      // Several item tables can be mounted at once; only the visible one takes focus.
      if (!rootRef.current || rootRef.current.offsetParent === null) return;
      // Scoped to this instance: the order screens mount several item tables across
      // mutually exclusive branches, and a document-wide lookup could reach a hidden one.
      const cell = rootRef.current?.querySelector<HTMLElement>(
        `[data-cell="${focusCell.rowIndex}:${focusCell.columnKey}"]`
      );
      if (!cell) return;
      cell.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = cell.matches("input, select")
        ? cell
        : cell.querySelector<HTMLElement>('input, select, [tabindex]:not([tabindex="-1"])');
      if (!focusable) return;
      // Same marker the focus hook uses: without it a dropdown caption reads this as the
      // user arriving and opens its list over the message we just wrote. The dropdowns
      // clear the attribute themselves; the timeout covers every other control.
      focusable.setAttribute(VALIDATION_FOCUS_ATTR, "");
      focusable.focus({ preventScroll: true });
      window.setTimeout(() => focusable.removeAttribute(VALIDATION_FOCUS_ATTR), 0);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusCell]);

  /* ------------------------------ UI ------------------------------ */

  return (
    <div
      key={`addFinalMileWrap${rowIndex}`}
      ref={rootRef}
      data-field={fieldKey}
    >
      <div className="addFinalMileWrap">
        {!data?.length && emptyMessage && (
          <span className="form__error">{emptyMessage}</span>
        )}
        {data?.length > 0 &&
          data.map((row, index) => {
            const isItemDescError = isEmpty(row.description);
            return (
              <div key={`orderDetailItemTable1${index}`}>
                <div className="addFinalMileWrap__row" key={`orderDetailItemTable${index}`}>
                  <div className="addFinalMileWrap__item">
                    <div className="addItemCard">
                      <div className="addItemCard__head">
                        <h3 className="title">Item #{row.serial}</h3>
                        {isEditable && (
                          <div className="btn__group">
                            <button
                              className="btn btn__primaryBlue"
                              onClick={() => addPackage(index)}
                              disabled={
                                !(
                                  row.packageType?.name === "PALLET" ||
                                  row.packageType?.name === "PLT" ||
                                  row.packageType?.name === "SKID" ||
                                  row.packageType === "PALLET" ||
                                  row.packageType === "PLT" ||
                                  row.packageType === "SKID"
                                )
                              }
                            >
                              <AddWhiteIcon />
                              Add Package
                            </button>
                            <button className="btn btn__danger" onClick={(e) => handleDelete(e, index, row)}>
                              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_8424_111155)">
                                  <path
                                    d="M2.0332 4.4694H3.36654M3.36654 4.4694H14.0332M3.36654 4.4694V13.8027C3.36654 14.1564 3.50701 14.4955 3.75706 14.7455C4.00711 14.9956 4.34625 15.1361 4.69987 15.1361H11.3665C11.7202 15.1361 12.0593 14.9956 12.3093 14.7455C12.5594 14.4955 12.6999 14.1564 12.6999 13.8027V4.4694H3.36654ZM5.36654 4.4694V3.13607C5.36654 2.78245 5.50701 2.44331 5.75706 2.19326C6.00711 1.94321 6.34625 1.80273 6.69987 1.80273H9.36654C9.72016 1.80273 10.0593 1.94321 10.3093 2.19326C10.5594 2.44331 10.6999 2.78245 10.6999 3.13607V4.4694"
                                    stroke="white"
                                    strokeWidth="1.67"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_8424_111155">
                                    <rect width="16" height="16" fill="white" transform="translate(0.0332031 0.470703)" />
                                  </clipPath>
                                </defs>
                              </svg>
                              Delete
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="addItemCard__body">
                        <div className="formRow formRow__2">
                          <div className="formCol">
                            <div className="formRow formRow__2">
                              <div className="formCol">
                                <div
                                  className="form__Field"
                                  data-cell={`${index}:description`}
                                >
                                  <label className="form__label">
                                    Description <span className="mandatory-mark">*</span>
                                  </label>

                                  <AddressDropDown
                                    error={cellMessage(index, "description")}
                                    columns={addressColumns}
                                    captionTitles="Search or type description"
                                    data={getRowDescList(index)}
                                    setData={makeRowDescSetter(index)}
                                    rowData={row.description ? { address: row.description } : null}
                                    setRowData={(full) => {
                                      const newData = [...data];
                                      const text = full?.address ?? "";
                                      const selectedId = full?.id ?? null;

                                      newData[index].description = text;
                                      newData[index].descriptionId = selectedId;

                                      if (full && !full.isManual) {
                                        newData[index] = applyTemplateToRow(newData[index], full);
                                      }

                                      newData[index].isModified = true;
                                      setData(newData);
                                    }}
                                    displayColumn="address"
                                    apiUrl={customerId ? "/customer/saveddetails/customeritemlist" : ""}
                                    // explicit: search by description param
                                    searchParamKey="description"
                                    type="dropdown"
                                    useFreeTextOnNoSelect={true}
                                    overwriteWithFreeText={true}
                                    enableRemove={isEditable}
                                    disable={!isEditable}
                                    labelTitle={undefined}
                                    mandatory={true}
                                    customerId={customerId}
                                  />

                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label">SKU Number </label>
                                  <input
                                    type="text"
                                    id="skuNumber"
                                    onChange={(e) => handleInputChange(e, index)}
                                    value={row?.skuNumber ?? ""}
                                    className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}`}
                                    placeholder="SKU Number"
                                    disabled={!isEditable}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="formCol">
                            <div className="formRow formRow__3">
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label">
                                    Handling Unit Amt <span className="mandatory-mark">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    id="handlingUnit"
                                    data-cell={`${index}:handlingUnit`}
                                    onChange={(e) => handleInputChange(e, index)}
                                    value={row.handlingUnit}
                                    className={`${!isEditable ? "form__input form__input--sm form__input--disabled" : "form__input form__input--sm"}${cellErrorClass(index, "handlingUnit")}`}
                                    placeholder="Handling unit amt"
                                    disabled={!isEditable}
                                  />
                                    {cellError(index, "handlingUnit")}
                                </div>
                              </div>
                              <div className="formCol">
                                <div
                                  className="form__Field"
                                  data-cell={`${index}:packageType`}
                                >
                                  <SingleDropdown
                                    error={cellMessage(index, "packageType")}
                                    fieldName="packageType"
                                    selectedItem={
                                      typeof row["packageType"] === "string"
                                        ? packageType.find(o => o.name?.toLowerCase() === row["packageType"]?.toLowerCase()) || null
                                        : row["packageType"] || null
                                    }
                                    setSelectedData={(_, item) => {
                                      handleDropdownChange({ target: { id: "packageType", value: item } }, index);
                                    }}
                                    staticData={packageType}
                                    labelTitle="Handling Unit Type"
                                    placeholder="-Select-"
                                    keyBindName="name"
                                    mandatory={true}
                                    disable={!isEditable}
                                    customStyle={{ minWidth: "1px" }}
                                  // autoOpenOnFocus={true}
                                  />
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="form__Field">
                                  <label className="form__label">
                                    Total Items/Pieces <span className="mandatory-mark">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    id="quantity"
                                    data-cell={`${index}:quantity`}
                                    onChange={(e) => handleInputChange(e, index)}
                                    value={row.quantity}
                                    className={`form__input form__input--sm ${!isEditable || row.package.length > 0 ? "form__input--disabled" : ""}${cellErrorClass(index, "quantity")}`}
                                    placeholder="0"
                                    disabled={!isEditable || row.package.length > 0}
                                  />
                                    {cellError(index, "quantity")}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="formCol">
                            <div className="formRow formRow__2">
                              <div className="formCol">
                                <div className="formRow formRow--flex">
                                  <div className="formCol">
                                    <div className="form__Field">
                                      <label className="form__label">
                                        Total Weight <span className="mandatory-mark">*</span>
                                      </label>
                                      <input
                                        type="number"
                                        id="weight"
                                        data-cell={`${index}:weight`}
                                        onChange={(e) => handleInputChange(e, index)}
                                        value={row.weight}
                                        className={`form__input form__input--sm ${!isEditable || row.package.length > 0 ? "form__input--disabled" : ""}${cellErrorClass(index, "weight")}`}
                                        placeholder="0"
                                        disabled={!isEditable || row.package.length > 0}
                                      />
                                        {cellError(index, "weight")}
                                    </div>
                                  </div>
                                  <div className="formCol">
                                    <div className="form__Field">
                                      <label className="form__label">Type</label>
                                      <select
                                        id="weightUnit"
                                        className={`form__select form__select--sm form__select--order ${!isEditable ? "form__select--disabled" : ""}`}
                                        value={row?.weightUnit?.id || row?.weightUnit}
                                        onChange={(e) => handleDropdownChange(e, index)}
                                        disabled={!isEditable}
                                      >
                                        <option value="lbs">LBS</option>
                                        <option value="kgs">KGS</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="formCol">
                                <div className="formRow formRow--flex">
                                  <div className="formCol">
                                    <div className="form__Field">
                                      <label className="form__label">
                                        Item Dimension <span className="mandatory-mark">*</span>
                                      </label>
                                      <div className="formRow formRow--flex">
                                        <div className="formCol">
                                          <div className="form__Field">
                                            <input
                                              type="number"
                                              id="length"
                                              data-cell={`${index}:length`}
                                              onChange={(e) => handleChildInputChange(e, index)}
                                              value={row.length || row.itemDimension.length}
                                              className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}${cellErrorClass(index, "length")}`}
                                              placeholder="L"
                                              disabled={!isEditable}
                                            />
                                          </div>
                                        </div>
                                        <div className="formCol">
                                          <div className="form__Field">
                                            <input
                                              type="number"
                                              id="width"
                                              data-cell={`${index}:width`}
                                              onChange={(e) => handleChildInputChange(e, index)}
                                              value={row.width || row.itemDimension.width}
                                              className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}${cellErrorClass(index, "width")}`}
                                              placeholder="W"
                                              disabled={!isEditable}
                                            />
                                          </div>
                                        </div>
                                        <div className="formCol">
                                          <div className="form__Field">
                                            <input
                                              type="number"
                                              id="height"
                                              data-cell={`${index}:height`}
                                              onChange={(e) => handleChildInputChange(e, index)}
                                              value={row.height || row.itemDimension.height}
                                              disabled={!isEditable}
                                              placeholder="H"
                                              className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}${cellErrorClass(index, "height")}`}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      {dimensionError(index)}
                                    </div>
                                  </div>
                                  <div className="formCol">
                                    <div className="form__Field">
                                      <label className="form__label">Unit</label>
                                      <select
                                        id="unit"
                                        className="form__select form__select--sm form__select--order form__select--disabled"
                                        value={row.unit?.id || row.unit}
                                        onChange={(e) => handleDropdownChange(e, index)}
                                        disabled={true}
                                      >
                                        <option value="IN">IN</option>
                                        <option value="CM">CM</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="formCol">
                            <div className="formRow">
                              <div className="formCol">
                                <div className="formRow formRow__3">
                                  <div className="formCol">
                                    {/* <div className="form__Field">
                                      <label className="form__label">
                                        Item Class <span className="mandatory-mark">*</span>
                                      </label>
                                      <select
                                        id="itemClass"
                                        className={`form__select form__select--sm form__select--order ${!isEditable ? "form__select--disabled" : ""}`}
                                        value={row.itemClass}
                                        onChange={(e) => handleDropdownChange(e, index)}
                                        disabled={!isEditable}
                                      >
                                        <option value="" disabled>
                                          -Select-
                                        </option>
                                        {FREIGHT_CLASS.map((item) => (
                                          <option key={item.name} value={item.name}>
                                            {item.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div> */}
                                    <div
                                      className="form__Field"
                                      data-cell={`${index}:itemClass`}
                                    >
                                      <label className="form__label">
                                        Item Class {orderType !== "Final Mile" && orderType !== "Expedited" && <span className="mandatory-mark">*</span>}
                                      </label>
                                      <SingleDropdown
                                        error={cellMessage(index, "itemClass")}
                                        fieldName="itemClass"
                                        selectedItem={
                                          row.itemClass
                                            ? { id: row.itemClass, name: row.itemClass }
                                            : null
                                        }
                                        setSelectedData={(_, item) => {
                                          handleDropdownChange({ target: { id: "itemClass", value: item?.name || "" } }, index);
                                        }}
                                        staticData={FREIGHT_CLASS}
                                        placeholder="-Select-"
                                        keyBindName="name"
                                        mandatory={orderType !== "Final Mile" && orderType !== "Expedited"}
                                        disable={!isEditable}
                                        customStyle={{ minWidth: "1px" }}
                                      // autoOpenOnFocus={true}
                                      />
                                      {row.suggestedClass ? (
                                        <div className="form__error">Suggested Class: {row.suggestedClass}</div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    {/* {row.suggestedClass ? (
                                      <div className="form__error">Suggested Class: {row.suggestedClass}</div>
                                    ) : (
                                      ""
                                    )} */}
                                  </div>
                                  <div className="formCol">
                                    <div className="form__Field">
                                      <label className="form__label">NMFC#</label>
                                      <input
                                        type="text"
                                        id="nmfc"
                                        data-cell={`${index}:nmfc`}
                                        onChange={(e) => handleInputChange(e, index)}
                                        value={row.nmfc}
                                        className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}${cellErrorClass(index, "nmfc")}`}
                                        placeholder="NMFC"
                                        disabled={!isEditable}
                                      />
                                        {cellError(index, "nmfc")}
                                    </div>
                                  </div>
                                  <div className="formRow formRow__2">
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">Stack</label>
                                        <select
                                          id="stackable"
                                          className={`form__select form__select--sm form__select--order ${!isEditable ? "form__select--disabled" : ""}`}
                                          onChange={(e) => handleDropdownChange(e, index)}
                                          value={isEditable ? row.stackable?.id : stackMapping[row.stackable]}
                                          disabled={!isEditable}
                                        >
                                          <option value="No">No</option>
                                          <option value="Yes">Yes</option>
                                        </select>
                                      </div>
                                    </div>
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">Linear Feet</label>
                                        <input
                                          type="text"
                                          id="linearFeet"
                                          disabled
                                          className={`form__input form__input--sm  form__input--disabled `}
                                          value={row.linearFeet}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>



                        {/* Hazmat */}
                        <div className="formRow">
                          <div className="formCol">
                            <div className="form__checkbox">
                              <div className="form__checkbox__option mb_0">
                                <input
                                  type="checkbox"
                                  id="hazmat"
                                  className="form__checkbox__input"
                                  checked={row.hazmat}
                                  onClick={(e: any) => {
                                    if (!isEditable) {
                                      setRowData(row);
                                      setRowIndex(index);
                                      setAddHazmatDetailsModal(true);
                                      return;
                                    }
                                    handleHazmatCheckboxChange(index);
                                    if (row.hazmat === true) {
                                      setRowData(row);
                                      setRowIndex(index);
                                      setAddHazmatDetailsModal(true);
                                    }
                                  }}
                                />
                                <label className="form__checkbox__label">Hazmat</label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ---------------- Packages ---------------- */}
                  {row?.package && row?.package?.length > 0
                    ? row?.package.map((packageRow: any, indexPackage: any) => {
                      const isPkgDescError = isEmpty(packageRow.description);
                      const pkgKey = `${index}-${indexPackage}`;
                      return (
                        <div className="addFinalMileWrap__item" key={`packageRow${indexPackage}`}>
                          <div className="addItemCard addItemCard--package">
                            <div className="addItemCard__head">
                              <h3 className="title">Package #{indexPackage + 1}</h3>
                              {isEditable && (
                                <div className="btn__group">
                                  <button className="btn btn__danger" onClick={(e) => handlePackageDelete(e, index, indexPackage)}>
                                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <g clipPath="url(#clip0_8424_111155)">
                                        <path
                                          d="M2.0332 4.4694H3.36654M3.36654 4.4694H14.0332M3.36654 4.4694V13.8027C3.36654 14.1564 3.50701 14.4955 3.75706 14.7455C4.00711 14.9956 4.34625 15.1361 4.69987 15.1361H11.3665C11.7202 15.1361 12.0593 14.9956 12.3093 14.7455C12.5594 14.4955 12.6999 14.1564 12.6999 13.8027V4.4694H3.36654ZM5.36654 4.4694V3.13607C5.36654 2.78245 5.50701 2.44331 5.75706 2.19326C6.00711 1.94321 6.34625 1.80273 6.69987 1.80273H9.36654C9.72016 1.80273 10.0593 1.94321 10.3093 2.19326C10.5594 2.44331 10.6999 2.78245 10.6999 3.13607V4.4694"
                                          stroke="white"
                                          strokeWidth="1.67"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_8424_111155">
                                          <rect width="16" height="16" fill="white" transform="translate(0.0332031 0.470703)" />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className="addItemCard__body">
                              <div className="formRow formRow__2">
                                <div className="formCol">
                                  <div className="formRow formRow__3">
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">Item/Piece Count <span className="mandatory-mark">*</span></label>
                                        <input
                                          type="number"
                                          id="quantity"
                                          className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}${packageCellErrorClass(index, indexPackage, "quantity")}`}
                                          placeholder="0"
                                          value={packageRow.quantity}
                                          onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                          disabled={!isEditable}
                                        />
                                        {packageCellError(index, indexPackage, "quantity")}
                                      </div>
                                    </div>
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">Item Weight <span className="mandatory-mark">*</span></label>
                                        <input
                                          type="number"
                                          id="weight"
                                          className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}${packageCellErrorClass(index, indexPackage, "weight")}`}
                                          placeholder="0"
                                          value={packageRow.weight}
                                          onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                          disabled={!isEditable}
                                        />
                                        {packageCellError(index, indexPackage, "weight")}
                                      </div>
                                    </div>
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">Item Dimension</label>
                                        <div className="formRow formRow--flex">
                                          <div className="formCol">
                                            <div className="form__Field">
                                              <input
                                                type="number"
                                                id="length"
                                                className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}`}
                                                placeholder="L"
                                                value={packageRow.length}
                                                onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                                disabled={!isEditable}
                                              />
                                            </div>
                                          </div>
                                          <div className="formCol">
                                            <div className="form__Field">
                                              <input
                                                type="number"
                                                id="width"
                                                className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}`}
                                                placeholder="W"
                                                value={packageRow.width}
                                                onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                                disabled={!isEditable}
                                              />
                                            </div>
                                          </div>
                                          <div className="formCol">
                                            <div className="form__Field">
                                              <input
                                                type="number"
                                                id="height"
                                                className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}`}
                                                placeholder="H"
                                                value={packageRow.height}
                                                onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                                disabled={!isEditable}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="formCol">
                                  <div className="formRow formRow__2">
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">Class</label>
                                        <select
                                          id="itemClass"
                                          className={`form__select form__select--sm ${!isEditable ? "form__select--disabled" : ""}`}
                                          value={packageRow.itemClass}
                                          disabled={!isEditable}
                                          onChange={(e) => handlePackageDropdownChange(e, index, indexPackage)}
                                        >
                                          <option value="" disabled>
                                            -Select-
                                          </option>
                                          {FREIGHT_CLASS.map((item) => (
                                            <option key={item.name} value={item.name}>
                                              {item.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      {packageRow.suggestedClass && isEditable ? (
                                        <div className="form__error">Suggested Class: {packageRow.suggestedClass}</div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div className="formCol">
                                      <div className="form__Field">
                                        <label className="form__label">NMFC</label>
                                        <input
                                          type="text"
                                          id="nmfc"
                                          className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}`}
                                          placeholder="NMFC"
                                          value={packageRow.nmfc}
                                          onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                          disabled={!isEditable}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Package Description with template hydrate */}
                              <div className="formRow formRow__2">
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">
                                      Item Description <span className="mandatory-mark">*</span>
                                    </label>

                                    <AddressDropDown
                                      error={packageCellMessage(index, indexPackage, "description")}
                                      columns={addressColumns}
                                      captionTitles="Search or type description"
                                      data={getPkgDescList(pkgKey)}
                                      setData={makePkgDescSetter(pkgKey)}
                                      rowData={
                                        packageRow.description ? { address: packageRow.description } : null
                                      }
                                      setRowData={(full) => {
                                        const cloned = [...data];
                                        const text = full?.address ?? "";
                                        const pkg = { ...cloned[index].package[indexPackage] };

                                        pkg.description = text;
                                        pkg.descriptionId = full?.id ?? null;
                                        if (full && !full.isManual) {
                                          cloned[index].package[indexPackage] =
                                            applyTemplateToPackageRow(pkg, full);

                                          // update totals on parent row if qty/weight changed
                                          const totals = calculateTotalForPackage(cloned[index]);
                                          cloned[index].quantity = totals.totalQuantity;
                                          cloned[index].weight = totals.totalWeight;
                                        } else {
                                          cloned[index].package[indexPackage] = pkg;
                                        }

                                        cloned[index].isModified = true;
                                        setData(cloned);
                                      }}
                                      displayColumn="address"
                                      apiUrl={customerId ? "/customer/saveddetails/customeritemlist" : ""}
                                      searchParamKey="description"
                                      useFreeTextOnNoSelect={true}
                                      overwriteWithFreeText={true}
                                      enableRemove={isEditable}
                                      disable={!isEditable}
                                      labelTitle={undefined}
                                      mandatory={true}
                                      customerId={customerId}
                                    />

                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label">SKU Number</label>
                                    <input
                                      type="text"
                                      id="skuNumber"
                                      value={packageRow.skuNumber ?? ""}
                                      onChange={(e) => handleInputChangeForPackage(e, index, indexPackage)}
                                      className={`form__input form__input--sm ${!isEditable ? "form__input--disabled" : ""}`}
                                      placeholder="SKU Number"
                                      disabled={!isEditable}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                    : ""}
                </div>
              </div>
            );
          })}
      </div>

      {addHazmatDetailsModal && (
        <AddHazmatDetailsModal
          setConfirmModal={setAddHazmatDetailsModal}
          data={data}
          setData={setData}
          rowData={rowData}
          rowIndex={rowIndex}
        />
      )}
    </div>
  );
};

export default OrderItemDetailTable;
